// resolveName.js

/**
 * Resolves a human-readable name from a FHIR reference.
 * Supports Practitioner and Patient references.
 *
 * @param {string} reference - FHIR reference string (e.g., "Practitioner/123", "Patient/456")
 * @param {object} store - Redux store or similar state container
 * @param {object} fhirClient - FHIR client instance for making requests
 * @returns {Promise<string|null>} - Resolved name or original reference
 */
async function resolveName(reference, store, fhirClient) {
  if (
    !reference ||
    typeof reference !== "string" ||
    reference.endsWith("/undefined")
  ) {
    return null;
  }

  const [resourceType, resourceId] = reference.split("/");
  if (!resourceType || !resourceId) return null;

  const state = store?.getState?.();
  let cached;

  if (resourceType === "Practitioner") {
    cached = state?.providers?.[resourceId];
  } else if (resourceType === "Patient") {
    cached = state?.patients?.[resourceId];
  } else {
    return reference; // unsupported resource type
  }

  if (cached?.name?.[0]) {
    const name = cached.name[0];
    const given = Array.isArray(name.given) ? name.given[0] : "";
    const family = name.family || "";
    const fullName = `${given} ${family}`.trim();
    return fullName || reference;
  }

  if (fhirClient) {
    try {
      const resource = await fhirClient.request(reference);
      const name = resource?.name?.[0];
      const given = Array.isArray(name?.given) ? name.given[0] : "";
      const family = name?.family || "";
      return `${given} ${family}`.trim() || reference;
    } catch (err) {
      console.warn("🔍 Failed to resolve name via FHIR:", reference, err);
      return reference;
    }
  }

  return reference;
}

module.exports = { resolveName };